import {Buffer} from 'buffer';

import DOMPurify from 'dompurify';
import {Base64} from 'js-base64';
import quotedPrintable from 'quoted-printable';

import {zoom} from '../proto/pbjs_crypto_message_pb';

import stats from './stats';

const TO = 'to';
const FROM = 'from';
const CC = 'cc';
const BCC = 'bcc';
const SUBJECT = 'subject';
const CONTENT_DISPOSITION = 'content-disposition';
const CONTENT_ID = 'content-id';
const CONTENT_TYPE = 'content-type';
const CONTENT_TRANSFER_ENCODING = 'content-transfer-encoding';
const MULTIPART_TYPE = {
  ALTERNATIVE: 'multipart/alternative',
  MIXED: 'multipart/mixed',
};
const TEXT_TYPE = {
  PLAIN: 'text/plain',
  HTML: 'text/html',
};

export const HEADER = {
  FROM,
  TO,
  CC,
  BCC,
  SUBJECT,
  CONTENT_DISPOSITION,
  CONTENT_TYPE,
  CONTENT_ID,
  CONTENT_TRANSFER_ENCODING,
};

// Mime Encoded Words (RFC 1342)
const QUOTED_PRINTABLE = 'q';
const BASE64 = 'b';
// eslint-disable-next-line max-len
const mimeEncReg = /=\?(?<charset>\S+?)\?(?<encoding>[QqBb])\?(?<text>\S+?)\?=/gm;


export const headerHasName = (header, desiredHeader) => {
  return header.name.toLowerCase() === desiredHeader.toLowerCase();
};

export const decodeHeader = (header) => {
  const matches = [...header.value.matchAll(mimeEncReg)];
  let decoded = '';
  let ind = 0;
  if (matches.length) {
    // has encoded mime words; iterate through words to construct
    // final decoded header value
    for (const match of matches) {
      // add  any plaintext between the last encoded word and this word
      const substr = header.value.slice(ind, match.index);
      if (substr.trim().length > 0) {
        decoded += substr;
      }
      ind = match.index + match[0].length;

      const charset = match.groups.charset.toLowerCase();
      const decoder = new TextDecoder(charset);
      const encoding = match.groups.encoding.toLowerCase();
      const text = match.groups.text;
      let wordBytes = Buffer.from('');
      if (encoding === QUOTED_PRINTABLE) {
        wordBytes = Buffer.from(quotedPrintable.decode(text), 'binary');
      } else if (encoding === BASE64) {
        wordBytes = Base64.toUint8Array(text);
      } else {
        throw new Error('Invalid mime header encoding');
      }
      decoded += decoder.decode(wordBytes);
    }
    // add any remaining plaintext after the last mime encoded word
    const substr = header.value.slice(ind, header.value.length);
    if (substr.trim().length > 0) {
      decoded += substr;
    }
  } else {
    // not mime word encoded; return value as is
    decoded = header.value;
  }
  return decoded;
};

/**
 *
 * @param {string} headerVal a header value string containing a value
 *  and any additional directives
 * @return {Object} an object containing the unpacked header value and
 *  its directives as key value pairs
 */
export const unpackHeaderValue = (headerVal) => {
  let value = '';
  let directives = [];
  const terms = headerVal.split(';');
  value = terms[0];
  directives = Object.fromEntries(
      terms.slice(1).map(
          (term) => term
              .replace(/"/g, '')
              .split('=')
              .map((t) => t.trim()),
      ),
  );
  return {
    value,
    directives,
  };
};

export const resolveInlines = (htmlString, cidURLMap) => {
  const msgDoc = new DOMParser().parseFromString(htmlString, TEXT_TYPE.HTML);
  const imgEls = msgDoc.getElementsByTagName('img');
  for (const imgNode of imgEls) {
    if (imgNode.src.startsWith('cid')) {
      // replace att src with attachment data url
      const cidInfo = cidURLMap[imgNode.src];
      if (cidInfo) {
        const {imgUrl, status} = cidInfo;
        if (status === 'LOADING') {
          // if imgNode hasn't been processed already
          if (!('zmailOriginalAlt' in imgNode.dataset)) {
            imgNode.dataset.zmailOriginalAlt = imgNode.alt || '';
            imgNode.alt = 'Loading...';
          }
        } else if (status === 'ERROR') {
          // keep the skeleton to ensure consistent formatting, but
          // reset the original alt text
          imgNode.alt = imgNode.dataset.zmailOriginalAlt;
        } else if (status === 'SUCCESS') {
          imgNode.alt = imgNode.dataset.zmailOriginalAlt;
          imgNode.src = imgUrl;
          removeSkeleton(imgNode);
        }
      }
    }
  }
  return msgDoc.body.innerHTML;
};

/**
 *
 * @param {MIMEPart} partPb a header value string containing a value
 *  and any additional directives
 * @param {string} headerName header to search for
 * @return {?Object} an object containing the unpacked header value and
 *  its directives as key value pairs or null if headerName can't be found
 */
export const lookupHeaderValue = (partPb, headerName) => {
  const header = partPb.headers.find(
      (header) => headerHasName(header, headerName));
  if (header) {
    return unpackHeaderValue(header.value);
  }
  return null;
};

/**
 *
 * @param {MIMEPart} attachmentPb MIMEPart pb with attachment populated
 * @return {Object} object containing the unpacked attachment data
 */
export const getAttachmentData = (attachmentPb) => {
  let mimeType;
  let contentId;
  let isInline = false;
  let filename = attachmentPb.attachment.filename;
  const decryptIndex = attachmentPb.attachment.index;
  for (const header of attachmentPb.headers) {
    if (headerHasName(header, CONTENT_DISPOSITION)) {
      const headerInfo = unpackHeaderValue(header.value);
      if (!filename && headerInfo.directives.filename) {
        filename = headerInfo.directives.filename;
      }
      if (headerInfo.value === 'inline') {
        isInline = true;
      }
    } else if (headerHasName(header, CONTENT_TYPE)) {
      const headerInfo = unpackHeaderValue(header.value);
      mimeType = headerInfo.value;
      if (!filename && headerInfo.directives.name) {
        filename = headerInfo.directives.name;
      }
    } else if (headerHasName(header, CONTENT_ID)) {
      const headerInfo = unpackHeaderValue(header.value);
      contentId = headerInfo.value;
    }
  }
  console.log(attachmentPb);
  return {
    id: decryptIndex,
    cid: contentId,
    isInline,
    filename,
    mimeType,
    decryptIndex,
    size: attachmentPb.attachment.size,
  };
};

/**
 *
 * @param {string} token JWT token string
 * @return {Object} the JWT payload
 */
export const readJWT = (token) => {
  try {
    const tokenParts = token.split('.');
    const decodedHeaders = Base64.decode(tokenParts[0]);
    const b64payload = tokenParts[1];
    const decodedPayload = Base64.decode(b64payload);
    return {
      headers: JSON.parse(decodedHeaders),
      payload: JSON.parse(decodedPayload),
    };
  } catch (err) {
    throw new Error('token parse failure');
  }
};

/**
 *
 * @param {string} filename
 * @param {Uint8Array} attachmentData att data as a byte array
 */
export const saveAsFile = stats.withMetric(
    {metric: stats.METRIC.GET_ATTACH, description: 'attDownload'},
    function saveAsFile(filename, attachmentData, mimeType) {
      const url = window.URL.createObjectURL(
          new Blob([attachmentData], {
            type: mimeType,
            responseType: 'arraybuffer',
          }),
      );
      const FileSaver = require('file-saver');
      // const file = new File([blob], filename, {type: mimeType});
      FileSaver.saveAs(url, filename);
    },
);

/**
 *
 * @param {MIMEPart} bodyPart MIMEPart pb
 * @return {string} html string that has been base64 encoded once
 */
export const readHtmlPart = (bodyPart) => {
  const contentEncoding = lookupHeaderValue(
      bodyPart,
      HEADER.CONTENT_TRANSFER_ENCODING);
  const bodyStr = Base64.fromUint8Array(bodyPart.body);
  if (contentEncoding?.value === 'base64') {
    return Base64.decode(bodyStr);
  }
  return bodyStr;
};

// Creates a placeholder node with the dimensions of the original img
// if present
const createSkeleton = (doc, imgEl) => {
  const imgSkeleton = doc.createElement('span');
  imgSkeleton.style.height = imgEl?.height || '20';
  imgSkeleton.style.width = imgEl?.width;
  imgSkeleton.style.display = 'inline-block';
  imgSkeleton.style.border = '1px solid #ccc';
  return imgSkeleton;
};

// Replace the given imgEl with a skeleton frame of same dimensions
// while cid image is loading
export const placeSkeleton = (doc, imgEl) => {
  const imgSkeleton = createSkeleton(doc, imgEl);
  imgEl.parentElement.replaceChild(imgSkeleton, imgEl);
  imgSkeleton.appendChild(imgEl);
};

// Cleans up the placed skeleton with the loaded img element
export const removeSkeleton = (imgEl) => {
  const skeletonImg = imgEl.parentElement;
  const parent = skeletonImg.parentElement;
  parent.replaceChild(imgEl, skeletonImg);
};

// Returns html string with inline attached images
// replaced with skeleton placeholders and the original
// html string on error
export const insertSkeletonImgs = (fullHtml) => {
  try {
    const domParser = new DOMParser();
    const msgDoc = domParser.parseFromString(fullHtml, TEXT_TYPE.HTML);
    const imgEls = msgDoc.getElementsByTagName('img');
    for (const imgEl of imgEls) {
      if (imgEl.src && imgEl.src.startsWith('cid:')) {
        placeSkeleton(msgDoc, imgEl);
      }
    }
    return msgDoc.body.innerHTML;
  } catch (e) {
    console.error('Failed to insert placeholders: ', e);
  }
  return fullHtml;
};

/**
 * Tries to separate the body html content and the quote content; if
 *  quote and body ids are not detected,
 *  returns the whole html as htmlBody
 * @param {string} fullHtml html string
 * @return {Object} separated htmlBody and htmlQuote html strings
 */
export const splitBodyQuote = (fullHtml) => {
  try {
    const domParser = new DOMParser();
    const msgDoc = domParser.parseFromString(fullHtml, TEXT_TYPE.HTML);
    const htmlBodyNode = msgDoc.children[0].children[1];
    const bodyContent = htmlBodyNode.children;
    if (bodyContent.length >= 2) {
      const bodyHtmlNode = bodyContent[0];
      const quoteHtmlNode = bodyContent[1];
      if (
        bodyHtmlNode.id === 'zoom_emc_body_container' &&
        quoteHtmlNode.id === 'zoom_emc_quote_container'
      ) {
        return {
          htmlBody: bodyHtmlNode.outerHTML,
          htmlQuote: quoteHtmlNode.outerHTML,
        };
      }
    }
  } catch (err) {
    console.error('Fail to separate body from quote:', err);
  }
  // Set html body back to the full content; no quote available
  return {
    htmlBody: fullHtml,
    htmlQuote: '',
  };
};

export const unpackData = stats.withMetric(
    {metric: stats.METRIC.GET_MSG, description: 'unpackMsg'},
    async (metadata, mimePbBytes) => {
      try {
        // unpack message
        const mimePart = zoom.mail.MIMEPart.decode(mimePbBytes);

        // true email attachments begin at index 2
        // (indices 0 and 1 point to the manifest and msg body)
        const attachmentOffset = 2;
        const attachmentMeta = metadata.parts.slice(attachmentOffset);
        const attachmentIds = attachmentMeta.map(
            (att) => att.body.attachmentId);

        let from = '';
        let to = '';
        let cc = '';
        let bcc = '';
        let subject = '';
        let attachments = [];
        let inlineAttachments = [];
        let htmlBody = '';
        let htmlQuote = '';

        // walk through metadata and extract relevant data for rendering
        for (const header of metadata.headers) {
          if (headerHasName(header, HEADER.FROM)) {
            from = header.value;
          }
          if (headerHasName(header, HEADER.TO)) {
            to = header.value.replace(/,/g, ', ');
          }
          if (headerHasName(header, HEADER.CC)) {
            cc = header.value.replaceAll(',', ', ');
          }
          if (headerHasName(header, HEADER.BCC)) {
            bcc = header.value.replaceAll(',', ', ');
          }
        }

        for (const header of mimePart.headers) {
          if (headerHasName(header, HEADER.SUBJECT)) {
            subject = decodeHeader(header);
          }
        }

        let encodedHtml = '';
        const attParts = [];
        const inlineAttParts = [];
        const contentType = lookupHeaderValue(mimePart,
            HEADER.CONTENT_TYPE)?.value;
        if (contentType === MULTIPART_TYPE.ALTERNATIVE) {
          // No attachments, only body content
          for (const bodyPart of mimePart.children.parts) {
            const contentType = lookupHeaderValue(
                bodyPart,
                HEADER.CONTENT_TYPE,
            );
            if (contentType.value === TEXT_TYPE.HTML) {
              encodedHtml += readHtmlPart(bodyPart);
            }
          }
        } else if (contentType === MULTIPART_TYPE.MIXED) {
          console.log('read atts');
          // MimePart contains attachments and body content
          for (const part of mimePart.children.parts) {
            if (part.attachment) {
              // read attachment
              const att = getAttachmentData(part);
              // get the attachmentId for fetching from the msg metadata object
              att.id = attachmentIds[att.decryptIndex];
              if (att.isInline) {
                inlineAttParts.push(att);
              } else {
                attParts.push(att);
              }
            } else {
              for (const bodyPart of part.children.parts) {
                const contentType = lookupHeaderValue(
                    bodyPart,
                    HEADER.CONTENT_TYPE,
                );
                if (contentType.value === TEXT_TYPE.HTML) {
                  encodedHtml += readHtmlPart(bodyPart);
                }
              }
            }
          }
        }
        const receivedParts = attParts.length + inlineAttParts.length;
        if (receivedParts !== attachmentIds.length) {
          throw new Error('mismatched number of attachments');
        }
        attachments = attParts;
        inlineAttachments = inlineAttParts;
        // Sanitize email html
        const fullHtml = DOMPurify.sanitize(Base64.decode(encodedHtml));
        const htmlWithImgSkeletons = insertSkeletonImgs(fullHtml);
        // Try to separate the body content and the quote content; if
        // quote and body ids are not detected,
        // falls back to rendering the whole html doc as-is
        const msgSections = splitBodyQuote(htmlWithImgSkeletons);
        htmlBody = msgSections.htmlBody;
        htmlQuote = msgSections.htmlQuote;

        return {
          from,
          to,
          cc,
          bcc,
          subject,
          htmlBody,
          htmlQuote,
          attachments,
          inlineAttachments,
        };
      } catch (error) {
        console.error(error);
        throw new Error('failed to read message');
      }
    },
);
